import React from 'react';
import { Box, Typography } from '@mui/material';
import logo from '../img/logo-outlined.svg';

const LOGO_WIDTH_PX = 110;

// logo-outlined.svg pads its viewBox by half the 8px stroke on every side, so
// the rendered image carries this much empty space below the glyphs. Derived
// from the asset's own numbers rather than measured once and pasted, so
// changing either the width or the stroke keeps the alignment honest.
const LOGO_VIEWBOX_WIDTH = 288.67;
const LOGO_STROKE_PAD = 4;
const LOGO_PAD_PX = (LOGO_STROKE_PAD * LOGO_WIDTH_PX / LOGO_VIEWBOX_WIDTH).toFixed(2);

// Flex baseline alignment uses each item's FIRST baseline, and for a block with
// no text -- the logo's anchor -- that is its bottom margin edge. So aligning on
// the baseline lines the label's text up with the foot of the wordmark, provided
// the anchor's own box ends exactly at the glyphs (see `link` below).
//
// Bottom-alignment cannot do this: it matches the boxes, and a text box extends
// a descender's worth below its baseline, leaving the label floating high.

// The label inherits the host page's text colour instead of taking the theme's
// `text.secondary`, a dark grey that vanishes on a dark page -- the same trap
// the unstroked wordmark falls into. It carries no brand colour, so inheriting
// is safe; the logo must not, which is why it is stroked instead. The opacity
// sits on the label alone so the wordmark stays at full strength.
const styles = {
    root: {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'flex-end',
        gap: 0.75,
        mt: 1.5,
        color: 'inherit'
    },
    label: {
        color: 'inherit',
        opacity: 0.65
    },
    // `block`, not `inline-flex`: an inline box would contribute its own text
    // baseline and defeat the bottom-margin-edge rule above. Zero line-height
    // stops the host page's leading from padding the anchor below the image.
    link: {
        display: 'block',
        fontSize: 0,
        lineHeight: 0
    },
    // The outline overhangs the glyphs, which sit flush against the wordmark's
    // original bounds, so logo-outlined.svg widens its own viewBox to fit. CSS
    // overflow cannot rescue that -- an SVG rasterises to its viewBox before
    // overflow applies.
    //
    // The width is a legibility floor, not a taste call: the outline scales with
    // the mark, and much below this size it lands on ~2.5 device pixels and
    // antialiases into a grey fringe. Thinning the outline makes that worse, not
    // better, since a finer line resolves to even fewer pixels.
    //
    // The negative margin trims the stroke clearance inside the viewBox so the
    // anchor's bottom edge -- its baseline, per the note above -- lands on the
    // glyphs rather than on empty padding. It belongs here rather than on the
    // anchor: flex baseline-aligns an item by its margin box, so a margin on the
    // anchor would move the whole item and change nothing.
    logo: {
        display: 'block',
        width: LOGO_WIDTH_PX,
        mb: `${-LOGO_PAD_PX}px`
    }
};

/**
 * Marks the widget as OmniGo's on a customer's own site.
 *
 * Only the logo is a link. A host theme's `a { color }` and
 * `a { text-decoration }` rules cannot reach inside an image, so the wordmark
 * renders on-brand wherever it lands; making the whole line an anchor would
 * expose the text to exactly that.
 *
 * Embed-only by design -- mounted from bootstrap rather than from a widget, so
 * it never reaches the shared components the website also renders.
 */
const Attribution = () => (
    <Box sx={styles.root}>
        <Typography variant="caption" sx={styles.label}>Powered by</Typography>
        <Box
            component="a"
            href="https://omnigoevents.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="OmniGo Events"
            sx={styles.link}
        >
            <Box component="img" src={logo} alt="OmniGo!" sx={styles.logo} />
        </Box>
    </Box>
);

export default Attribution;
